import { useQueries, useQuery } from '@tanstack/react-query'
import { logEvent, LogLevel } from '@praxis/component-logging'
import {
  departmentsEndpoint,
  getDepartments,
} from '../../services/getDepartments'
import { fetchLocationCapacity } from 'services/getLocationCapacity'
import {
  fetchDestinationsForPoAndDeptEndpoint,
  transformPurchaseOrderDetails,
} from 'services/fetchDestinationsForPoAndDept'
import { fetchIRDDateEndpoint, transformIRD } from 'services/fetchIRD'
import {
  getLocationsAndProducts,
  transformLocations,
  transformProducts,
} from 'services/getLocationsAndProducts'
import { getShortestTransitTime } from '../../services/shortestTransitTime'
import {
  BookingTypes,
  PurchaseOrder,
  RawShipmentDetails,
  ReverseDestination,
  ShipmentRowInterface,
  TloAlert,
  VendorId,
  PoStatusResponse,
  ReverseCarriersRateParams,
} from 'common/types'
import {
  fetchPurchaseOrder,
  transformPaginationMetadata,
  transformPurchaseOrders,
} from '../../services/getPurchaseOrders'
import { getReverseCarriers } from '../../services/getReverseCarriers'
import { vendorAttributesEndpoint } from '../../services/vendorAttributes'
import { getDestinationByVendorIdsEndpoint } from '../../services/fetchDestinationsForVendorIds'
import { getShipmentsNeedReviewByVendorTloRefId } from 'services/shipmentsByTloRefId'
import { buildTloGroupSummary } from 'services/tloGroupSummary'
import { getShipmentsByFilters } from 'services/shipmentsByFilters'
import { PO_BOOKING_TYPES } from 'common/constants'
import { fetchReverseOriginLocations } from '../../services/fetchReverseOriginLocations'
import { ReverseOriginResponse } from '../../components/FormInputs/ReverseOrigin'
import {
  fetchReverseDestinations,
  ReverseDestinationResponse,
} from '../../services/fetchReverseDestinations'
import { getLoadsByFilters } from 'services/getLoadsByFilters'
import { fetchPoStatusByFilters } from 'services/fetchPoStatus'

export const useDepartmentsQuery = (vendorIds?: number[]) => {
  const departmentsQuery = useQuery(
    ['departments', vendorIds?.join()],
    () => getDepartments(vendorIds),
    {
      refetchOnWindowFocus: false, // default: true
    },
  )

  if (departmentsQuery.isError) {
    logEvent(
      {
        message: JSON.stringify({
          endpoint: departmentsEndpoint,
          error: departmentsQuery.error,
          message: 'Failed to fetch departments',
          type: 'REQUEST_FAILURE',
          user: localStorage?.userInfo,
        }),
        url: window.location.href,
      },
      { level: LogLevel.Error },
    )
  }
  return departmentsQuery
}

export const useShortestTransitTimeQuery = (
  vendorId: number,
  vop: number,
  department: string | number,
  destLocCode: string,
) => {
  return useQuery(
    [vendorId, vop, department, destLocCode],
    () => getShortestTransitTime(vendorId, vop, department, destLocCode),
    {
      refetchOnWindowFocus: false, // default: true
    },
  )
}

export const useMaxCapacityByVendorLocationPickupDateQuery = (
  locationId: string | undefined,
  vendorId: undefined | number,
  pickupDate: string | undefined,
  hasDiffPickupDate: boolean,
  bookingTypes: BookingTypes,
) => {
  const { data, isError, isLoading } = useQuery({
    queryKey: [
      `location-capacity-${locationId}-${vendorId || ''}-${pickupDate}`,
    ],
    queryFn: () => fetchLocationCapacity(locationId, vendorId, pickupDate),
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
      const status = error?.response?.status
      if (typeof status === 'number' && status >= 400 && status < 500) {
        return false
      }
      return failureCount < 3
    },
    enabled:
      hasDiffPickupDate &&
      !!locationId &&
      !!pickupDate &&
      !!vendorId &&
      bookingTypes.some((type) => PO_BOOKING_TYPES.includes(type)),
  })

  return { data, isError, isLoading }
}

export const useSameLoadShipmentsQuery = (
  loadId: number | string,
  bookingTypes: BookingTypes,
) => {
  return useQuery(['useSameLoadShipments', loadId], {
    queryFn: async () => {
      if (
        bookingTypes.some((type) => PO_BOOKING_TYPES.includes(type)) &&
        loadId
      ) {
        const result = await getShipmentsByFilters({
          bookingTypes: bookingTypes,
          loadIds: [loadId],
        })
        return result?.list
      } else {
        return []
      }
    },
    refetchOnWindowFocus: false,
  })
}

export const useLocationsProductsQuery = (
  vendorList: Array<String>,
  enableUseLocationProducts: boolean,
  onSuccess: ((data: any) => void) | undefined,
  onError: ((err: Error) => void) | undefined,
) => {
  const queryResult = useQuery(
    ['location-product', vendorList],
    () => getLocationsAndProducts(vendorList),
    {
      onSuccess,
      onError,
      select: (response: any) => {
        return {
          locations: transformLocations(response.data.vendor_locations),
          products: transformProducts(
            response.data.products,
            response.data.vendor_locations,
          ),
        }
      },
      enabled: enableUseLocationProducts,
      refetchOnWindowFocus: false,
    },
  )

  return {
    ...queryResult,
    isLoading: enableUseLocationProducts ? queryResult.isLoading : false,
  }
}

export const useShipmentByPOKeyQuery = (
  po?: PurchaseOrder,
  department?: string,
  vendorId?: VendorId,
  shipmentIds?: Array<string>,
  loadIds?: Array<string>,
  truckLoadOptimizationRefIds?: Array<string>,
  dateFrom?: string,
  dateTo?: string,
  bookingCategories?: Array<string>,
  destinationLocCode?: Array<string>,
  currentPage: number = 1,
  itemsPerPage: number = 100,
) => {
  return useQuery(
    [
      'shipments-by-po',
      po,
      department,
      vendorId,
      shipmentIds,
      loadIds,
      truckLoadOptimizationRefIds,
      dateFrom,
      dateTo,
      bookingCategories,
      destinationLocCode,
      currentPage,
      itemsPerPage,
    ],
    () => {
      return getShipmentsByFilters({
        bookingTypes: PO_BOOKING_TYPES,
        poIds: po ? [po] : undefined,
        department: department,
        vendorIds: vendorId ? [vendorId] : undefined,
        shipmentIds: shipmentIds,
        loadIds: loadIds,
        truckLoadOptimizationRefIds: truckLoadOptimizationRefIds,
        dateFrom: dateFrom,
        dateTo: dateTo,
        bookingCategories: bookingCategories,
        destinationLocCode: destinationLocCode,
        currentPage: currentPage,
        itemsPerPage: itemsPerPage,
      })
    },
    {
      refetchOnWindowFocus: false,
    },
  )
}

export const useShipmentByFiltersQuery = (
  bookingTypes: BookingTypes,
  vendorIds?: VendorId[],
  shipmentIds?: Array<string>,
  loadIds?: Array<string>,
  truckLoadOptimizationRefIds?: Array<string>,
  dateFrom?: string,
  dateTo?: string,
  bookingCategories?: Array<string>,
  destinationLocCode?: Array<string>,
  originLocCodes?: Array<string>,
  returnAuthorizationNumbers?: Array<string>,
  reverseLogisticsReferenceNumbers?: Array<string>,
  currentPage: number = 1,
  itemsPerPage: number = 100,
) => {
  return useQuery(
    [
      'shipments-by-filters',
      bookingTypes,
      vendorIds,
      shipmentIds,
      loadIds,
      truckLoadOptimizationRefIds,
      dateFrom,
      dateTo,
      bookingCategories,
      destinationLocCode,
      originLocCodes,
      returnAuthorizationNumbers,
      reverseLogisticsReferenceNumbers,
      currentPage,
      itemsPerPage,
    ],
    () =>
      getShipmentsByFilters({
        bookingTypes: bookingTypes,
        vendorIds: vendorIds,
        shipmentIds: shipmentIds,
        loadIds: loadIds,
        truckLoadOptimizationRefIds: truckLoadOptimizationRefIds,
        dateFrom: dateFrom,
        dateTo: dateTo,
        bookingCategories: bookingCategories,
        destinationLocCode: destinationLocCode,
        originLocCodes: originLocCodes,
        returnAuthorizationNumbers: returnAuthorizationNumbers,
        reverseLogisticsReferenceNumbers: reverseLogisticsReferenceNumbers,
        currentPage: currentPage,
        itemsPerPage: itemsPerPage,
      }),
    {
      refetchOnWindowFocus: false,
    },
  )
}

export const usePurchaseOrderQuery = (
  onSuccess: ((data: any) => void) | undefined,
  onError: ((err: Error) => void) | undefined,
  enableUsePurchaseOrder: boolean,
  vendorIds?: Array<string>,
  purchaseOrders?: Array<string>,
  dateFrom?: string,
  dateTo?: string,
  shipmentIds?: Array<string>,
  loadIds?: Array<string>,
  departmentIds?: Array<string>,
  tmLocIds?: Array<string>,
  bookingCategories?: Array<string>,
  currentPage = 1,
  itemsPerPage = 10,
  destinationsLocCodes?: Array<string>,
  truckLoadOptimizationRefIds?: Array<string>,
) => {
  return useQuery(
    [
      'purchase_orders',
      enableUsePurchaseOrder,
      vendorIds,
      purchaseOrders,
      dateFrom,
      dateTo,
      shipmentIds,
      loadIds,
      departmentIds,
      tmLocIds,
      bookingCategories,
      currentPage,
      itemsPerPage,
      destinationsLocCodes,
      truckLoadOptimizationRefIds,
    ],
    () =>
      fetchPurchaseOrder(
        vendorIds,
        purchaseOrders,
        dateFrom,
        dateTo,
        shipmentIds,
        loadIds,
        departmentIds,
        tmLocIds,
        bookingCategories,
        truckLoadOptimizationRefIds,
        currentPage,
        itemsPerPage,
        destinationsLocCodes,
      ),
    {
      onSuccess,
      onError,
      select: (data: any) => {
        return {
          rawData: data,
          purchaseOrders: transformPurchaseOrders(data?.purchase_orders || []),
          paginationMetadata: transformPaginationMetadata(
            data?.pagination_metadata || {},
          ),
        }
      },
      enabled: enableUsePurchaseOrder,
      refetchOnWindowFocus: false,
    },
  )
}

export const useLoadsSearch = (
  dateFrom: string = '',
  dateTo: string = '',
  vendorIds: string[] = [],
  tmLocIds: string[] = [],
  loadIds: string[] = [],
  currentPage: number = 1,
  itemsPerPage: number = 10,
  onSuccess: (data: any) => void,
  onError: (err: Error) => void,
) => {
  return useQuery(
    [
      'loads-search',
      dateFrom,
      dateTo,
      vendorIds,
      tmLocIds,
      loadIds,
      itemsPerPage,
      currentPage,
    ],
    () =>
      getLoadsByFilters(
        {
          start_from_date: dateFrom,
          start_to_date: dateTo,
          vendor_ids: vendorIds,
          location_ids: tmLocIds,
          load_ids: loadIds,
        },
        currentPage,
        itemsPerPage,
      ),
    {
      enabled:
        (vendorIds.length > 0 && !!dateFrom && !!dateTo) ||
        (tmLocIds.length > 0 && !!dateFrom && !!dateTo) ||
        loadIds.length > 0,
      refetchOnWindowFocus: false,
      onError: onError,
      onSuccess: onSuccess,
    },
  )
}

export const useRetrieveDestinationsByPoAndDeptQuery = (
  po?: number | null,
  dept?: number | null,
) => {
  return useQuery(
    ['dc-locations', po, dept],
    () => fetchDestinationsForPoAndDeptEndpoint(po, dept),
    {
      select: (data: any) => {
        return transformPurchaseOrderDetails(data?.data)
      },
      refetchOnWindowFocus: false,
    },
  )
}

export const useVendorAttributesQuery = (
  vendorId: VendorId,
  onError: ((err: Error) => void) | undefined,
  enabled: boolean = true,
) => {
  const queryResult = useQuery(
    ['vendor-attribute', vendorId],
    () => vendorAttributesEndpoint(vendorId),
    {
      select: (data: any) => {
        return {
          usePalletVolume: data?.data?.use_vendor_pallet_volume,
          businessHrsBasedPickup: data?.data?.business_hrs_based_pickup,
        }
      },
      refetchOnWindowFocus: false,
      onError,
      enabled,
    },
  )

  return {
    ...queryResult,
    isLoading: enabled ? queryResult.isLoading : false,
  }
}

export const useRetrieveIRDDateQuery = (
  po?: number | null,
  dept?: string | null,
) => {
  return useQuery(
    ['ird-dates', po, dept],
    () => fetchIRDDateEndpoint(po, dept),
    {
      select: (data: any) => {
        return transformIRD(data?.data)
      },
      refetchOnWindowFocus: false,
    },
  )
}

export const useDestinationByVendorIdsQuery = (
  bookingTypes: BookingTypes,
  origins?: string[],
  vendorIds?: number[],
) => {
  const destinationsForVendorIdsQuery = useQuery(
    ['destinations-vendorIds', vendorIds, bookingTypes, origins],
    () => getDestinationByVendorIdsEndpoint(vendorIds, bookingTypes, origins),
    {
      refetchOnWindowFocus: false, // default: true
    },
  )

  if (destinationsForVendorIdsQuery.isError) {
    logEvent(
      {
        message: JSON.stringify({
          endpoint: getDestinationByVendorIdsEndpoint,
          error: destinationsForVendorIdsQuery.error,
          message: 'Failed to fetch destinations for vendorIds',
          type: 'REQUEST_FAILURE',
          user: localStorage?.userInfo,
        }),
        url: window.location.href,
      },
      { level: LogLevel.Error },
    )
  }
  return destinationsForVendorIdsQuery
}

export const useShipmentsByTloAlerts = (
  vendorIds: number[],
  shipments: ShipmentRowInterface[],
) => {
  // Splitting the provided shipments into groups by tlo_ref_id
  const shipmentsByTLO: Record<string, ShipmentRowInterface[]> =
    shipments.reduce(
      (
        acc: Record<string, ShipmentRowInterface[]>,
        shipment: ShipmentRowInterface,
      ) => {
        if (!shipment.tloRefId) {
          return acc
        }

        if (acc[shipment.tloRefId]) {
          acc[shipment.tloRefId] = [...acc[shipment.tloRefId], shipment]
        } else {
          acc[shipment.tloRefId] = [shipment]
        }

        return acc
      },
      {},
    )

  // Extracting unique TLO ids
  const tloIds = Object.keys(shipmentsByTLO)

  const tloShipmentQueries =
    vendorIds?.length && tloIds?.length
      ? tloIds.map((id) => ({
          queryKey: [`shipmentsByTlo ${id}`],
          queryFn: () => getShipmentsNeedReviewByVendorTloRefId(vendorIds, id),
          select: (data: {
            list: RawShipmentDetails[]
            pagination_metadata: {
              current_page: number
              total_count: number
              total_pages: number
            }
          }): TloAlert | undefined => {
            // Comparison to see if there are more shipments in the API response
            const currentShipmentsById = new Map(
              shipmentsByTLO[id].map((shipment) => [
                shipment.tgtBookingId || shipment.bookingId,
                shipment,
              ]),
            )
            const allShipments = data.list.map((shipment) => {
              const current = currentShipmentsById.get(
                shipment.tgt_booking_id || shipment.booking_id,
              )
              return {
                totalWt: current?.totalWt ?? shipment.total_wt,
                totalVol: current?.totalVol ?? shipment.total_vol,
                pallet: current?.pallet ?? shipment.pallet,
              }
            })
            const responseShipmentIds = new Set(
              data.list.map(
                (shipment) => shipment.tgt_booking_id || shipment.booking_id,
              ),
            )
            shipmentsByTLO[id].forEach((shipment) => {
              if (
                !responseShipmentIds.has(
                  shipment.tgtBookingId || shipment.bookingId,
                )
              ) {
                allShipments.push({
                  totalWt: shipment.totalWt,
                  totalVol: shipment.totalVol,
                  pallet: shipment.pallet,
                })
              }
            })
            const summary = buildTloGroupSummary(id, allShipments)
            const currentShipmentIds = new Set(
              shipmentsByTLO[id].map((shipment) =>
                String(shipment.tgtBookingId || shipment.bookingId),
              ),
            )
            const hasOtherTloShipments = data.list.some(
              (shipment) =>
                !currentShipmentIds.has(
                  String(shipment.tgt_booking_id || shipment.booking_id),
                ),
            )
            const otherShipmentCount = Math.max(
              data.list.filter(
                (shipment) =>
                  !currentShipmentIds.has(
                    String(shipment.tgt_booking_id || shipment.booking_id),
                  ),
              ).length,
              data.pagination_metadata.total_count - shipmentsByTLO[id].length,
              0,
            )
            const isIncomplete =
              hasOtherTloShipments ||
              data.pagination_metadata.total_count > shipmentsByTLO[id].length

            if (summary.isOverCapacity || isIncomplete) {
              return {
                id: id,
                heading: summary.isOverCapacity
                  ? 'TLO Group Exceeds Single Truck Capacity'
                  : 'TLO Shipments Incomplete',
                message: summary.isOverCapacity
                  ? 'The shipments in this TLO group exceed single-truck capacity.'
                  : `There are ${otherShipmentCount} other shipments part of TLO ${id} that must be reviewed before they all can be successfully routed.`,
                type: summary.isOverCapacity ? 'error' : 'alert',
                autoHideDuration: 20000,
                summary,
                isIncomplete,
                incompleteMessage: isIncomplete
                  ? `There are ${otherShipmentCount} other shipments part of TLO ${id} that must be reviewed before they all can be successfully routed.`
                  : undefined,
              }
            }
          },
          refetchOnWindowFocus: false, // default: true
        }))
      : []

  return useQueries({ queries: tloShipmentQueries })
}

export const useReverseOriginLocationsQuery = (
  successHandler?: (data: ReverseOriginResponse[]) => void,
) => {
  return useQuery({
    queryKey: ['reverse-origin-locations'],
    queryFn: () => fetchReverseOriginLocations(),
    onSuccess: successHandler,
    refetchOnWindowFocus: false, // default: true
    staleTime: Infinity,
  })
}

export const useRetrieveReverseDestinationsQuery = (
  bookingTypes?: BookingTypes,
) => {
  return useQuery({
    queryKey: ['reverse-destinations'],
    queryFn: () => fetchReverseDestinations(),
    select: (data: ReverseDestinationResponse[]) => {
      return data.map((destination): ReverseDestination => {
        return {
          tmLocId: destination.tm_loc_id,
          type: destination.type,
          locationId: destination.location_id,
          vendorId: destination.vendor_id,
          vendorName: destination.vendor_name,
          name: destination.name,
          ...(destination.address
            ? {
                address: {
                  addressLine: destination.address.address_line,
                  addressLineTwo: destination.address.address_line_two,
                  city: destination.address.city,
                  state: destination.address.state,
                  zipcode: destination.address.zipcode,
                  countryCode: destination.address.country_code,
                },
              }
            : {}),
          returnPolicyIds: destination.return_policy_ids,
          active: destination.active,
        }
      })
    },
    refetchOnWindowFocus: false, // default: true
    staleTime: 43200000, // 12 hours
    enabled: !bookingTypes || bookingTypes.includes('REVERSE_LOGISTICS_MANUAL'),
  })
}

export const usePoStatusSearch = (
  po: string,
  onSuccess: (data: PoStatusResponse) => void,
  onError: (err: Error) => void,
) => {
  return useQuery(
    ['po-status-search', po],
    async () => {
      const response = (await fetchPoStatusByFilters(po)) as {
        data: PoStatusResponse
      }
      return response.data
    },
    {
      enabled: po !== '',
      refetchOnWindowFocus: false,
      onError: onError,
      onSuccess: onSuccess,
    },
  )
}
export const useReverseCarriersQuery = (
  params: ReverseCarriersRateParams,
  enabled: boolean = true,
  successHandler?: (data: {
    carrier_list: { carrier_id: string; carrier_name?: string }[]
  }) => void,
) => {
  return useQuery(
    ['reverse-carriers', params],
    () => getReverseCarriers(params),
    {
      onSuccess: successHandler,
      refetchOnWindowFocus: false, // default: true
      staleTime: Infinity,
      enabled,
    },
  )
}
